import React, { DOMAttributes, useCallback } from 'react'
import styled, { css } from 'styled-components'
import { SvgDropdown } from '@chilipiper/icons/src/design-system'
import { Icon } from '../../../../old/icon/Icon'
import { OverflowPillWithMeasurer } from '../../input/pill-combobox/OverflowPill'
import { SettingLabel } from '../../wayfinding/setting-label/SettingLabel'
import { UnstyledButton } from '../../../core-components/unstyled-button/UnstyledButton'
import type { AriaProps } from '../../types'
import { Flex, Grid, Text } from '../../../core-components'
import { ItemData } from '../base-select/itemData.types'
import { CorePill } from './CorePill'

type Item = ItemData & { value: string; isVisible: boolean }
interface Props {
  invisibleValuesCount?: number
  isDisabled?: boolean
  isWrapping: boolean
  onChange?: (value: Array<string>) => void
  pillsWrapperRef?: React.RefObject<HTMLDivElement | null>
  placeholder?: string
  values: ReadonlyArray<string>
  items: Array<Item>
}

export const Pills = ({
  pillsWrapperRef,
  values,
  invisibleValuesCount = 0,
  isDisabled,
  onChange,
  placeholder,
  isWrapping,
  items,
}: Props) => {
  return (
    <Flex
      alignItems='center'
      gap={2}
      flexGrow={1}
      flexWrap={isWrapping ? 'wrap' : undefined}
      minWidth={0 /* to make textOverflow: ellipsis work */}
      ref={pillsWrapperRef}
    >
      {values.length === 0 && placeholder ? (
        <Text textStyle='form-value' color='fg/minor' py='7px' pl={3}>
          {placeholder}
        </Text>
      ) : null}
      {items.map(item => {
        const {
          startContent,
          children,
          dataTestId,
          dataId,
          isDisabled: isPillDisabled,
          textValue,
        } = item

        return (
          <Pill
            key={item.value}
            isDisabled={isDisabled || isPillDisabled}
            onChange={onChange}
            values={values}
            value={item.value}
            startContent={startContent}
            data-test-id={dataTestId}
            data-id={dataId}
            textValue={textValue}
            isVisible={item.isVisible}
            isTheOnlyVisible={Boolean(items.at(0)?.isVisible && !items.at(1)?.isVisible)}
            isWrapping={isWrapping}
          >
            {children}
          </Pill>
        )
      })}
      <OverflowPillWithMeasurer
        invisibleValuesCount={invisibleValuesCount}
        totalValuesCount={values.length}
      />
    </Flex>
  )
}

const Pill = React.memo(
  ({
    onChange,
    values,
    value,
    ...corePillProps
  }: React.ComponentPropsWithoutRef<typeof CorePill> & {
    onChange?: (value: Array<string>) => void
    values: ReadonlyArray<string>
    value: string
  }) => {
    const handleRemove = useCallback(() => {
      onChange?.(values.filter(oldValue => oldValue !== value))
    }, [onChange, values, value])

    return <CorePill {...corePillProps} onRemove={onChange ? handleRemove : undefined} />
  }
)
Pill.displayName = 'Pill'

export const SelectContainer = ({
  children,
  'data-test-id': dataTestId,
  variant,
  isDisabled,
  ariaProps,
  isOpen,
  h,
  triggerRef,
  rootProps,
}: {
  ariaProps?: AriaProps
  children: React.ReactNode
  'data-test-id'?: string
  h?: number
  isDisabled?: boolean
  isOpen: boolean
  rootProps?: DOMAttributes<unknown>
  triggerRef?: React.RefObject<HTMLDivElement | null>
  variant: 'regular' | 'nested'
}) => {
  return (
    <StyledGrid isOpen={isOpen} h={h} position='relative' {...rootProps} ref={triggerRef}>
      <StyledButton
        gridArea='1/-1'
        data-test-id={dataTestId}
        disabled={isDisabled}
        gap={2}
        bg={variant === 'regular' ? 'bg/form-item-default' : 'transparent'}
        boxShadow='border/on-major'
        data-variant={variant}
        ariaProps={ariaProps}
      />
      <StyledDisableEventsFlex
        zIndex={1}
        gridArea='1/-1'
        pl={2}
        pr={5}
        py={variant === 'nested' ? '10px' : '6px'}
        justifyContent='space-between'
        minWidth={0 /* to make textOverflow: ellipsis work */}
      >
        {children}
        <StyledIcon
          ml={6}
          mt='6px'
          isOpen={isOpen}
          flexShrink={0}
          icon={SvgDropdown}
          color='fg/minor'
        />
      </StyledDisableEventsFlex>
    </StyledGrid>
  )
}
const StyledGrid = styled(Grid)<{ isOpen: boolean }>`
  overflow: hidden;

  ${({ isOpen }) =>
    isOpen &&
    css`
      overflow: visible;
      height: auto;
    `}
  :focus-within,
  :hover {
    overflow: visible;
    height: auto;
  }
`
const StyledIcon = styled(Icon)<{ isOpen: boolean }>`
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : undefined)};
`
const StyledDisableEventsFlex = styled(Flex)`
  pointer-events: none;
`
const StyledButton = styled(UnstyledButton)`
  border-radius: ${({ theme }) => theme.radii[10]};

  &[data-variant='nested'] {
    border-radius: ${({ theme }) => theme.radii['none']};

    ::after {
      box-shadow: none;
    }
  }

  :hover {
    background: ${({ theme }) => theme.colors['bg/form-item-hovered']};

    ::after {
      box-shadow: none;
    }
  }

  :focus-visible {
    background: ${({ theme }) => theme.colors['bg/form-item-hovered']};

    ::after {
      box-shadow: ${({ theme }) => theme.shadows['border/focus-indicator']};
    }
  }

  &[aria-expanded='true'] {
    background: ${({ theme }) => theme.colors['bg/form-item-focused']};

    ::after {
      box-shadow: ${({ theme }) => theme.shadows['border/form-item-focused']};
    }
  }

  :disabled {
    background: ${({ theme }) => theme.colors['bg/disabled']};

    ::after {
      box-shadow: none;
    }
  }
`
export const Label = (props: React.ComponentProps<typeof SettingLabel>) => (
  <SettingLabel {...props} />
)
